// src/utils/documentLineTax.js — taxlines5-a4c81e37 (tax codes on every line, Stage S5)
//
// A document's tax when the company's Settings › Taxes switch is ON — for every web editor: the estimate
// page (S4, through estimateLineTax.js, which re-exports this file) and the four older pop-ups (S5:
// estimate, invoice, job, recurring invoice). The full invoice / job / recurring pages inherit it.
// Pure (no Firebase), so every rule here is tested without the app.
//
// THREE MODES, decided from the live settings/taxCodes doc (lineTax.taxCodesSwitchOn):
//   'off'   — Taxes off: EVERY real company today. The editor runs exactly the code it ran before; nothing
//             in this file is used for it. While the doc is loading or can't be read, the legacy list
//             decides, so a Taxes-off company never waits and never fails a save here.
//             (S8: a Taxes-ON company whose settings can't be read must then block saving.)
//   'qb'    — Taxes on + QuickBooks: today's QuickBooks picker (taxCodeId), plus "Set tax for all
//             lines", the customer exemption and the stored tax parts.
//   'named' — Taxes on, no QuickBooks: the company's Settings › Taxes codes (salesTaxCodeId + the kept
//             copy salesTaxSnapshot, lineTax.js), "Stop using" codes never offered to new lines.
// The totals and the stored fields are the SERVER's (functions/utils/serverTotals.engineTotals): the
// same stamping, the same engine input, the same exemption stamp — so the shadow checker agrees.
import {
    taxCodesSwitchOn, lineTaxStatus, lineTaxChoices, applyLineTax, newLineTaxFields, stampLineTax,
    exemptionsSwitchOn, customerExemptionOf, taxPartLabel, NO_TAX, STOPPED_NOTE,
    fieldTaxCheckOf, fieldTaxCheckMark, fieldTaxCheckCount, clearFieldTaxCheck, FIELD_TAX_CHECK_TEXT,
    carryExemptionOff,
} from './lineTax';
import { computeDocumentTotals, makeTaxCodeResolver, namedTaxLookups } from './totalsEngine';
import { estimateTaxContext, taxChoicesFor, applyTaxChoice, lineTaxValue, EXEMPT_CHOICE } from './estimateEditor';
import { canUseCatalog } from './catalogUi';
import { formatCurrency } from './formatters';
import { startingTaxCodes } from './taxCodes';
import { resolveDocTerminology } from './quoteSchema';

export const DOCUMENT_TAX_MARKER = 'taxlines5-a4c81e37';
export const TAX_MODES = Object.freeze({ OFF: 'off', QB: 'qb', NAMED: 'named' });
export { FIELD_TAX_CHECK_TEXT, STOPPED_NOTE };
export const SET_ALL_DONE = 'All lines updated. Change any line on its own below.';
export const TAX_SETTINGS_LOADING = 'Loading your tax settings…';

/** The page's tax mode. taxCodesDoc: undefined while loading, null when missing (useTaxCodesDoc). */
export function pageTaxMode({ orgId, taxCodesDoc, qbConnected }) {
    if (!taxCodesSwitchOn(orgId, taxCodesDoc === undefined ? null : taxCodesDoc)) return TAX_MODES.OFF;
    return qbConnected === true ? TAX_MODES.QB : TAX_MODES.NAMED;
}

/**
 * Who gets a tax picker (decision 6): office roles (Owner, Admin, Manager, Office Staff — the catalog
 * list) and Sales Consultants. Field Technicians and Lead Hands never.
 */
export const canPickLineTax = (role) => canUseCatalog(role) || role === 'Sales Consultant';
/** Who sees "Added in the field — check tax", and whose save clears it: office roles only. */
export const isOfficeTaxRole = (role) => canUseCatalog(role);

/**
 * Everything the engine needs for an ON mode. qb: today's QuickBooks resolver (estimateTaxContext);
 * named: the Settings › Taxes lookups (as serverTotals.engineResolver). The customer's exemption only
 * when exemptions are on.
 */
export function onModeContext({ mode, orgId, companySettings, taxOptions, taxLoaded, taxCodesDoc, customer }) {
    const doc = taxCodesDoc || null;
    // As the server (serverTotals.taxContext): the saved codes, else the carry-over code from the company rate.
    const namedCodes = startingTaxCodes(doc, companySettings || {}) || [];
    const legacyRate = Number(companySettings?.taxRate) || 0;
    const resolveTaxCode = mode === TAX_MODES.NAMED
        ? makeTaxCodeResolver(namedTaxLookups({ namedCodes, legacyRate }))
        : estimateTaxContext({ companySettings, taxOptions, taxLoaded }).resolveTaxCode;
    const exemption = exemptionsSwitchOn(orgId, doc) ? customerExemptionOf(customer) : null;
    return { mode, namedCodes, legacyRate, resolveTaxCode, exemption };
}

const isLine = (l) => l !== null && typeof l === 'object' && !Array.isArray(l);
const withoutKeys = (l, keys) => { const o = { ...l }; keys.forEach((k) => delete o[k]); return o; };
const thousandths = (r) => Math.round(Number(r) * 1000);
const partsRate = (parts) => (Array.isArray(parts) ? parts : []).reduce((t, p) => t + thousandths(p && p.rate), 0) / 1000;

/**
 * The lines as an ON-mode save stores them: named → every taxed line gets a code + kept copy
 * (lineTax.stampLineTax — an older line takes the default it was shown as); qb → as they are.
 * opts.restamp (recurring) — a line's kept copy is dropped first, so it takes its code's rates NOW.
 */
export function stampedLines(lines, ctx, opts) {
    const list = Array.isArray(lines) ? lines : [];
    if (ctx.mode !== TAX_MODES.NAMED) return list;
    const restamp = !!(opts && opts.restamp);
    return list.map((l) => {
        if (!isLine(l)) return l;
        return stampLineTax(restamp && l.salesTaxSnapshot ? withoutKeys(l, ['salesTaxSnapshot']) : l, ctx.namedCodes);
    });
}

/**
 * The totals for an ON mode = serverTotals.engineTotals: stamped lines, taxSnapshot left out of the
 * engine's input, the customer's exemption unless the document switched it off.
 *   opts.labelLines — invoices / recurring invoices: each line's taxSnapshot = { name, rate } of the code
 *                     it was charged ("Exempt" / 0 for No tax; exemptParts when the customer is exempt
 *                     from some of its parts), exactly as the server labels them.
 *   opts.restamp    — recurring invoices: kept copies dropped (current rates).
 *   opts.discount   — passed to the engine as the server does (none of the pop-ups has one).
 * → { totals (the engine's answer), lines (to store), storedFields, exemptionApplied }
 * Without opts every figure and line is exactly what S4's estimate page used.
 */
export function onModeTotals(lines, ctx, doc, opts) {
    const o = opts || {};
    const stamped = stampedLines(lines, ctx, o);
    const bare = stamped.map((l) => (isLine(l) ? withoutKeys(l, ['taxSnapshot']) : l));
    const ex = ctx.exemption || null;
    const off = doc && doc.taxExemptionOff ? doc.taxExemptionOff : undefined;
    const input = o.discount !== undefined ? { lineItems: bare, discount: o.discount } : { lineItems: bare };
    const totals = ex
        ? computeDocumentTotals({ ...input, taxExemptionOff: off }, { resolveTaxCode: ctx.resolveTaxCode, customerExemption: ex })
        : computeDocumentTotals(input, { resolveTaxCode: ctx.resolveTaxCode });
    const applied = !!ex && !totals.notes.some((n) => n.code === 'exemptionSwitchedOff');
    const storedFields = applied
        ? { taxParts: totals.taxParts, totalsEngineVersion: totals.engineVersion, taxExemptionApplied: { parts: ex.parts, number: ex.number } }
        : { taxParts: totals.taxParts, totalsEngineVersion: totals.engineVersion };
    const out = o.labelLines ? labelledLines(stamped, bare, ctx, totals, applied) : stamped;
    return { totals, lines: out, storedFields, exemptionApplied: applied };
}

// serverTotals.engineTotals's labelLines step, line for line.
function labelledLines(stamped, bare, ctx, totals, applied) {
    const exemptedOf = {};
    if (applied) totals.lines.forEach((rl) => { if (rl.exemptedParts && rl.exemptedParts.length) exemptedOf[rl.index] = rl.exemptedParts; });
    return stamped.map((l, i) => {
        if (!isLine(l)) return l;
        if (l.taxExempt) return { ...l, taxSnapshot: { name: 'Exempt', rate: 0 } };
        const c = ctx.resolveTaxCode(bare[i]).code;
        const xk = exemptedOf[i];
        if (xk) return { ...l, taxSnapshot: { name: c.name, rate: partsRate(c.components), exemptParts: (c.components || []).filter((p) => xk.includes(p.key)).map((p) => p.name) } };
        return { ...l, taxSnapshot: { name: c.name, rate: partsRate(c.components) } };
    });
}

/**
 * An ON-mode save's tax fields over the write (the estimate page, S4): lines stamped (and an office
 * role's save clears the field markers), the stored parts / engine version / exemption stamp. An edit
 * whose exemption no longer applies deletes the old stamp (deleteValue). Changed in place and returned.
 */
export function applyOnModeSave(write, { result, ctx, isEdit, isOfficeRole, deleteValue }) {
    write.lineItems = clearFieldTaxCheck(stampedLines(write.lineItems, ctx), isOfficeRole === true);
    write.subtotal = result.totals.subtotal;
    write.taxAmount = result.totals.taxAmount;
    write.total = result.totals.total;
    Object.assign(write, result.storedFields);
    if (isEdit && !result.exemptionApplied) write.taxExemptionApplied = deleteValue;
    return write;
}

/** How each kind of document is totalled and stored (the server's rule for the same kind). */
export const DOCUMENT_TAX_KINDS = Object.freeze({
    quote: { opts: {}, word: 'estimate' },
    invoice: { opts: { labelLines: true }, word: 'invoice' },
    job: { opts: {}, word: 'job' },
    recurring: { opts: { labelLines: true, restamp: true }, word: 'recurring invoice' },
});

/** The totals a pop-up shows for its lines (the kind's server rule). */
export const documentTotals = (lines, ctx, doc, kind) => onModeTotals(lines, ctx, doc, (DOCUMENT_TAX_KINDS[kind] || DOCUMENT_TAX_KINDS.quote).opts);

/**
 * An ON-mode save from one of the older pop-ups, over its write (write.lineItems = the lines it is
 * about to store). Worked out from those very lines, so the stored figures are what the server would
 * make of them:
 *   quote   — as the estimate page: lines stamped, subtotal / taxAmount / total, taxParts,
 *             totalsEngineVersion, taxExemptionApplied (deleted on an edit it no longer applies to).
 *   invoice — the same + each line's taxSnapshot label (the server's invoice rule).
 *   job     — lines stamped; invoiceTotal = the engine's total. Jobs carry no tax rows (as bookNow's jobs).
 *   recurring — lines keep only their code (no kept copy: every invoice made from the schedule takes the
 *             code's rates on the day, S1) + the taxSnapshot label at today's rates; no totals stored.
 * An office role's save clears the field markers; anyone else's keeps them.
 * → the engine's result (for the caller's own use). The write is changed in place.
 */
export function applyDocumentTaxSave(write, { kind, ctx, doc, isEdit, isOfficeRole, deleteValue }) {
    const k = DOCUMENT_TAX_KINDS[kind] ? kind : 'quote';
    const result = documentTotals(write.lineItems, ctx, doc, k);
    let lines = clearFieldTaxCheck(result.lines, isOfficeRole === true);
    if (k === 'recurring') lines = lines.map((l) => (isLine(l) && 'salesTaxSnapshot' in l ? withoutKeys(l, ['salesTaxSnapshot']) : l));
    write.lineItems = lines;
    if (k === 'job') { write.invoiceTotal = result.totals.total; return result; }
    if (k === 'recurring') return result;
    write.subtotal = result.totals.subtotal;
    write.taxAmount = result.totals.taxAmount;
    write.total = result.totals.total;
    Object.assign(write, result.storedFields);
    if (isEdit && !result.exemptionApplied) write.taxExemptionApplied = deleteValue;
    return result;
}

// ── the picker ──────────────────────────────────────────────────────────────

/**
 * One line's Tax dropdown in an ON mode → { value, choices: [{ value, label }], stopped, note, exempt }.
 * qb: today's QuickBooks choices. named: lineTax's choices; an older line shows the default; a line on a
 * stopped code shows it, marked, with the note.
 */
export function linePicker(line, ctx, taxCtx) {
    if (ctx.mode === TAX_MODES.QB) {
        const value = lineTaxValue(line);
        return { value, choices: taxChoicesFor(line, taxCtx), stopped: false, note: '', exempt: value === EXEMPT_CHOICE };
    }
    const status = lineTaxStatus(line, ctx.namedCodes);
    const choices = lineTaxChoices(ctx.namedCodes, line).map((c) => ({ value: c.value, label: c.label }));
    // No default code at all: an older line is charged the company rate — say so instead of a blank box.
    if (status.value === '') choices.unshift({ value: '', label: `Company ${ctx.legacyRate}%` });
    return { value: status.value, choices, stopped: status.stopped, note: status.note, exempt: status.value === NO_TAX };
}

/** A Tax dropdown choice applied to one line (ON modes). */
export function applyLinePick(line, value, ctx) {
    if (ctx.mode === TAX_MODES.QB) return applyTaxChoice(line, value);
    if (value === '') return line;
    return applyLineTax(line, value, ctx.namedCodes);
}

/** "Set tax for all lines": the choices a NEW line could have (no stopped codes) + No tax. */
export function setAllChoices(ctx, taxCtx) {
    if (ctx.mode === TAX_MODES.QB) return taxChoicesFor({}, taxCtx);
    return lineTaxChoices(ctx.namedCodes, null).map((c) => ({ value: c.value, label: c.label }));
}

/** Every line set to the chosen code (an ordinary edit; each line can still be changed on its own). */
export function setAllLines(lines, value, ctx) {
    return (Array.isArray(lines) ? lines : []).map((l) => (isLine(l) ? applyLinePick(l, value, ctx) : l));
}

// ── new lines ───────────────────────────────────────────────────────────────

/** A new line takes the catalog item's own code (else the company default) — named mode only. */
export function withItemSalesTax(line, item, ctx) {
    if (ctx.mode !== TAX_MODES.NAMED) return line;
    const base = withoutKeys(line, ['salesTaxCodeId', 'salesTaxSnapshot']);
    return { ...base, ...newLineTaxFields(ctx.namedCodes, item) };
}

/**
 * A line someone who can't pick tax adds (a Field Technician or Lead Hand, decision 6): the catalog
 * item's code (else the company default) in named mode, and the "Added in the field — check tax"
 * marker in both ON modes. atMs = when.
 */
export function fieldAddedLine(line, item, ctx, atMs) {
    return { ...withItemSalesTax(line, item, ctx), taxCheck: fieldTaxCheckMark(atMs) };
}

// ── converts ────────────────────────────────────────────────────────────────

/**
 * The switch-off a NEW document made by a convert button carries from its source (the server's
 * taxExemption.carriedOff): only when exemptions are on for the company and the source has one.
 * from = { kind: 'quote'|'job', id, label }. → the taxExemptionOff to store, or null.
 */
export function convertedExemptionOff({ orgId, taxCodesDoc, source, from }) {
    if (!exemptionsSwitchOn(orgId, taxCodesDoc === undefined ? null : taxCodesDoc)) return null;
    return carryExemptionOff(source, from);
}

/** Where a convert's switch-off came from, as people read it: "estimate #1042" / "job #0042" (the server's docLabel words). */
export function convertSourceOf(source) {
    const s = source || {};
    if (s.eventType === 'Job') return { kind: 'job', id: s.id, label: s.jobNumber ? `job #${s.jobNumber}` : 'the job' };
    const word = resolveDocTerminology(s.docType).word.toLowerCase();
    return { kind: 'quote', id: s.id, label: s.quoteNumber ? `${word} #${s.quoteNumber}` : `the ${word}` };
}

/**
 * The fields a convert button adds to the NEW document's seed: { taxExemptionOff } carried from the source
 * (estimate → job, estimate → invoice, job → invoice), or {} — always {} unless Settings › Taxes and
 * exemptions are on for the company, so every other company's seed is exactly as before.
 */
export function carriedExemptionOffFields({ orgId, taxCodesDoc, source }) {
    const off = convertedExemptionOff({ orgId, taxCodesDoc, source, from: convertSourceOf(source) });
    return off ? { taxExemptionOff: off } : {};
}

/**
 * The tax fields a line added on site (the job quick view's "Add Item On-Site") gets: {} with Taxes off
 * (every real company — the line is exactly as before). Taxes on: the company default code (named mode),
 * and when the person can't pick tax (Field Technician, Lead Hand) the "Added in the field — check tax"
 * marker. atMs = when.
 */
export function onSiteLineTaxFields({ orgId, taxCodesDoc, companySettings, role, atMs }) {
    const mode = pageTaxMode({ orgId, taxCodesDoc, qbConnected: companySettings?.quickbooksConnected === true });
    if (mode === TAX_MODES.OFF) return {};
    const fields = mode === TAX_MODES.NAMED ? newLineTaxFields(startingTaxCodes(taxCodesDoc || null, companySettings || {}) || [], null) : {};
    return canPickLineTax(role) ? fields : { ...fields, taxCheck: fieldTaxCheckMark(atMs) };
}

// ── what the editors say ────────────────────────────────────────────────────

/** A totals row's label in an ON mode: "GST 5%", or "GST 5% — exempt #123" for an exempted part. */
export function onModeRowLabel(p) {
    const label = taxPartLabel(p);
    if (!p || !p.exempt) return label;
    const n = p.exempt && typeof p.exempt.number === 'string' ? p.exempt.number : '';
    return `${label} — exempt${n ? ` #${n}` : ''}`;
}

/**
 * Answer 1 (Marcus 2026-11-03): an older document re-totalled by a save. When the document's stored
 * lines, totalled the way a save now would, don't give the stored tax, saving changes its tax:
 * "Saving will change the tax on this estimate from $59.00 to $95.40." (newTax = the editor's figure now).
 * null when nothing would change, for a new document, or when the stored tax isn't a number.
 */
export function taxChangeNote({ source, storedLinesTax, newTax, currency, docWordLower }) {
    if (!source || !source.id || typeof source.taxAmount !== 'number' || !isFinite(source.taxAmount)) return null;
    const cents = (v) => Math.round(Number(v) * 100);
    if (cents(storedLinesTax) === cents(source.taxAmount)) return null;
    if (cents(newTax) === cents(source.taxAmount)) return null;
    return `Saving will change the tax on this ${docWordLower || 'estimate'} from ${formatCurrency(source.taxAmount, currency)} to ${formatCurrency(newTax, currency)}.`;
}

/** The confirm's title for that note: "Change the tax on this invoice?" */
export const taxChangeTitle = (docWordLower) => `Change the tax on this ${docWordLower || 'estimate'}?`;

/**
 * A job's stored tax for the note: a job stores only invoiceTotal (= subtotal + tax). → the tax it
 * holds, or undefined when it holds no total.
 */
export function jobStoredTax(job, lines) {
    if (!job || typeof job.invoiceTotal !== 'number' || !isFinite(job.invoiceTotal)) return undefined;
    const sub = (Array.isArray(lines) ? lines : []).reduce((t, l) => t + (isLine(l) ? (Number(l.quantity) || 0) * (Number(l.price) || 0) : 0), 0);
    return job.invoiceTotal - sub;
}

/** The field-marker count above the lines: "2 lines were added in the field — check the tax before saving." */
export const fieldMarksText = (n) => `${n === 1 ? '1 line was' : `${n} lines were`} added in the field — check the tax before saving.`;

/** Recurring schedules: no switch-off on the schedule itself (answer 3) — what the read-only note says. */
export const RECURRING_EXEMPTION_NOTE = 'Each invoice this schedule makes applies the exemption. To charge tax on one invoice, open that invoice.';

/**
 * What a line's tax depends on, as text — to tell whether an editor's lines still match the stored
 * document (the exemption banner's server call re-totals the STORED document, so it waits for a save).
 */
export function linesFingerprint(lines) {
    return JSON.stringify((Array.isArray(lines) ? lines : []).filter(isLine).map((l) => [
        String(l.name || ''), Number(l.quantity) || 0, Number(l.price) || 0, !!l.taxExempt,
        String(l.taxCodeId || ''), String(l.salesTaxCodeId || ''), String(l.serviceId || ''),
    ]));
}

/**
 * Why the pop-up's "Charge tax on this document" / "Apply exemption again" must wait, or null:
 * a new document ("Save the invoice first…"), or lines / customer changed since it was opened.
 */
export function exemptionBlockedReason({ source, lines, customerId, sourceCustomerId, word }) {
    if (!source || !source.id) return `Save the ${word || 'document'} first to change its exemption.`;
    if (linesFingerprint(lines) !== linesFingerprint(source.lineItems) || String(customerId || '') !== String(sourceCustomerId || '')) return 'Save your changes first.';
    return null;
}

/** target with `fields` taken from data (a field data no longer has is removed). → a new object. */
export function overlayFields(target, data, fields) {
    const out = { ...(target || {}) };
    (fields || []).forEach((k) => { if (data && k in data) out[k] = data[k]; else delete out[k]; });
    return out;
}

export { fieldTaxCheckOf, fieldTaxCheckCount };
